import type {
  CampoDeTerreno,
  ItemDeTerreno,
  NotasDeUnaConstruccion,
  NotasGeneralesDeTerreno,
  RegistroDeTerreno,
  SeccionDeTerreno,
  SincronizacionDeTerreno,
} from "@/lib/terreno";

import { fotosDelRegistro, pluralizar } from "./registroDeTerreno";

/* ---------------------------------------------------------------------------
   La lógica PURA de las sincronizaciones de terreno: cómo se rotula cada una y
   qué deja pasar el filtro de la pestaña "Registro de terreno".

   Una sincronización es una corrida de la App (lo que el encargado mandó de una
   vez). El filtro de esta pestaña OCULTA lo que no llegó en la elegida; las demás
   pestañas solo RESALTAN (ver `resaltado.ts`), nunca esconden.
--------------------------------------------------------------------------- */

const ZONA_HORARIA = "America/Santiago";

const PARTES_DEL_DIA = new Intl.DateTimeFormat("es-CL", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: ZONA_HORARIA,
});

function partes(iso: string): Record<string, string> | null {
  const momento = new Date(iso);
  if (Number.isNaN(momento.getTime())) return null;

  return Object.fromEntries(PARTES_DEL_DIA.formatToParts(momento).map((p) => [p.type, p.value]));
}

/** "09:12": solo la hora, en hora de Chile. */
export function horaDeSincronizacion(iso: string): string {
  const p = partes(iso);

  return p ? `${p.hour}:${p.minute}` : "—";
}

/**
 * "05 jul · 09:12": día de dos dígitos, mes corto en minúsculas y SIN el punto
 * con que `es-CL` abrevia ("sept."), y la hora de 24 h.
 */
export function etiquetaDeSincronizacion(iso: string): string {
  const p = partes(iso);
  if (!p) return "—";

  return `${p.day} ${p.month.replace(".", "").toLowerCase()} · ${p.hour}:${p.minute}`;
}

/** Cuántas sincronizaciones llegaron después de la última revisión. */
export function cuantasSincronizacionesNuevas(sincronizaciones: SincronizacionDeTerreno[]): number {
  return sincronizaciones.filter((s) => s.nueva).length;
}

/**
 * El texto de estado junto a los chips: qué se está viendo o cuánto falta por
 * revisar. `elegida` es el id de la sincronización filtrada, o `null` en "Todas".
 */
export function textoDeEstadoDeSincronizaciones(
  sincronizaciones: SincronizacionDeTerreno[],
  elegida: string | null,
): string {
  const seleccionada = elegida ? sincronizaciones.find((s) => s.id === elegida) : undefined;
  if (seleccionada) {
    return `Mostrando solo lo recibido en la sincronización del ${etiquetaDeSincronizacion(seleccionada.en)}.`;
  }

  const nuevas = cuantasSincronizacionesNuevas(sincronizaciones);
  if (nuevas === 0) return "Todo revisado.";

  return `${pluralizar(nuevas, "sincronización nueva", "sincronizaciones nuevas")} desde su última revisión.`;
}

/* ---- El filtro ---------------------------------------------------------- */

function filtrarCampo(campo: CampoDeTerreno, id: string): CampoDeTerreno | null {
  const filtrado: CampoDeTerreno = {
    ...campo,
    textos: campo.textos.filter((t) => t.sincronizacion_id === id),
    audios: campo.audios.filter((a) => a.sincronizacion_id === id),
    fotos: campo.fotos.filter((f) => f.sincronizacion_id === id),
  };

  // La coordenada del predio y la portada se ven SIEMPRE: son el contexto de
  // todo lo demás y no "llegan" en una sincronización que convenga aislar.
  const algo =
    filtrado.textos.length + filtrado.audios.length + filtrado.fotos.length > 0 ||
    campo.gps !== null ||
    campo.portada !== null;

  return algo ? filtrado : null;
}

/**
 * Un registro junta una foto, un texto y notas de voz que pueden haber llegado
 * en corridas distintas (cada parte trae la suya): se queda con lo que llegó en
 * la elegida, y desaparece si no queda nada.
 */
function filtrarRegistro(r: RegistroDeTerreno, id: string): RegistroDeTerreno | null {
  const fotos = fotosDelRegistro(r).filter((f) => f.sincronizacion_id === id);
  const foto = fotos[0] ?? null;
  const texto = r.texto !== null && r.sincronizacion_id === id ? r.texto : null;
  const audios = r.audios.filter((a) => a.sincronizacion_id === id);

  return fotos.length > 0 || texto !== null || audios.length > 0
    ? { ...r, foto, fotos, texto, audios }
    : null;
}

function filtrarRegistros(registros: RegistroDeTerreno[], id: string): RegistroDeTerreno[] {
  return registros.flatMap((r) => {
    const f = filtrarRegistro(r, id);

    return f ? [f] : [];
  });
}

function filtrarNotas(
  notas: NotasDeUnaConstruccion | undefined,
  id: string,
): NotasDeUnaConstruccion | undefined {
  if (!notas) return notas;

  return Object.fromEntries(
    Object.entries(notas).filter(([, nota]) => nota?.sincronizacion_id === id),
  ) as NotasDeUnaConstruccion;
}

function filtrarItem(item: ItemDeTerreno, id: string): ItemDeTerreno | null {
  const registros = filtrarRegistros(item.registros, id);
  const notas = filtrarNotas(item.notas, id);
  const conNotas = notas !== undefined && Object.keys(notas).length > 0;

  return registros.length > 0 || conNotas ? { ...item, registros, notas } : null;
}

/**
 * Deja solo lo que llegó en la sincronización `id` y quita los campos, ítems y
 * secciones que se quedan sin nada. La coordenada GPS y la portada nunca se
 * filtran. Lo anterior a las sincronizaciones (`sincronizacion_id` nulo) solo se
 * ve en "Todas". Con `id` nulo devuelve la MISMA lista.
 */
export function filtrarPorSincronizacion(
  secciones: SeccionDeTerreno[],
  id: string | null,
): SeccionDeTerreno[] {
  if (id === null) return secciones;

  return secciones.flatMap((seccion) => {
    const campos = seccion.campos.flatMap((c) => {
      const f = filtrarCampo(c, id);

      return f ? [f] : [];
    });
    const items = seccion.items.flatMap((i) => {
      const f = filtrarItem(i, id);

      return f ? [f] : [];
    });
    const capturas = filtrarRegistros(seccion.capturas, id);
    // Las ya clasificadas en la web viajan aparte de `capturas`: se filtran igual.
    const clasificadas =
      seccion.clasificadas_en_la_web && filtrarRegistros(seccion.clasificadas_en_la_web, id);
    const nClasificadas = clasificadas ? clasificadas.length : 0;

    return campos.length + items.length + capturas.length + nClasificadas > 0
      ? [
          {
            ...seccion,
            campos,
            items,
            capturas,
            ...(clasificadas ? { clasificadas_en_la_web: clasificadas } : {}),
          },
        ]
      : [];
  });
}

/**
 * Las notas generales del proyecto llegan en la sincronización en que llegó su
 * texto o cada audio: el filtro deja solo lo de la elegida (lo anterior a las
 * sincronizaciones solo se ve en "Todas"). Con `id` nulo devuelve el MISMO objeto.
 */
export function filtrarNotasGeneralesPorSincronizacion(
  notas: NotasGeneralesDeTerreno,
  id: string | null,
): NotasGeneralesDeTerreno {
  if (id === null) return notas;

  return {
    ...notas,
    texto: notas.texto && notas.texto.sincronizacion_id === id ? notas.texto : null,
    audios: notas.audios.filter((a) => a.sincronizacion_id === id),
  };
}
